import { JSX, splitProps, ParentComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import styles from "./Heading.module.css";

/**
 * Heading Variants
 *
 * Semantic levels (h1-h6) map to HTML elements
 * Visual variants control styling independently of semantic meaning
 */
export type HeadingVariant =
  // Page-level headings
  | "pageTitle" // Large page title (H1)
  | "pageTitleHome" // Large page title for Home (H1)
  | "pageSubline" // Large page subline (H2)
  | "sectionTitle" // Section headings (H2, uppercase)
  | "cardTitle" // Card/component headings (H3)
  | "subtitle" // Smaller headings (H4-H6)
  | "errorTitle" // Error page title (H1, red)
  | "heroOverline" // Host emoji section title
  | "formHeaderTitle" // Contact page title (H1, white)
  | "formHeaderSubtitle" // Contact page subtitle (H2, white)
  | "faqTitle" // FAQ section title (H2)
  | "teaserCardTitle" // Teaser card headline (H3)
  | "teaserBrandHeadline" // Teaser card headline (H3)

  // Specialized variants
  | "footerGroupTitle" // Footer/sidebar column titles
  | "emojiTitle" // Host emoji section title
  | "contentHeadline" // Content block headlines (large, white)
  | "contentOverline" // Content block overline (large, white)
  | "accordionTitle" // Accordion item titles
  | "slideHeadline" // Slider headlines
  | "slideSubheadline" // Slider subheadlines
  | "heroPageTitleHome" // Hero title on home page (large, white, above cover image)
  | "documentSubtitle" // Document download subtitle (purple overline)
  | "documentTitle" // Document download title (large dark headline)
  | "faqItemTitle" // FAQ accordion item title

  // Badge/label headings
  | "badgeLabel" // Small uppercase labels

  // Default semantic
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6";

/**
 * Props for the Heading component
 */
export interface HeadingProps {
  /**
   * Visual variant controlling the styling
   * @default "h2"
   */
  variant?: HeadingVariant;

  /**
   * Semantic HTML element to render
   * If not specified, will be inferred from variant
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span";

  /**
   * Center align the text
   */
  center?: boolean;

  /**
   * Add custom CSS classes
   */
  class?: string;

  /**
   * classList for conditional classes
   */
  classList?: Record<string, boolean>;

  /**
   * ID for the heading (useful for anchor links)
   */
  id?: string;

  /**
   * Content to render
   */
  children?: JSX.Element;
}

/**
 * Map variants to their default semantic HTML elements
 */
const variantToElementMap: Record<HeadingVariant, "h1" | "h2" | "h3" | "h4" | "h5" | "h6"> = {
  // Page-level
  pageTitle: "h1",
  pageTitleHome: "h1",
  pageSubline: "h2",
  slideHeadline: "h1",
  errorTitle: "h1",
  heroOverline: "h2",
  formHeaderTitle: "h1",
  formHeaderSubtitle: "h2",
  faqTitle: "h2",
  teaserCardTitle: "h3",
  teaserBrandHeadline: "h3",

  // Section-level
  sectionTitle: "h2",
  contentHeadline: "h2",
  contentOverline: "h3",
  documentSubtitle: "h3",
  documentTitle: "h2",
  heroPageTitleHome: "h1",

  // Card-level
  cardTitle: "h3",
  emojiTitle: "h3",
  accordionTitle: "h3",
  slideSubheadline: "h3",
  faqItemTitle: "h3",

  // Smaller
  subtitle: "h4",
  footerGroupTitle: "h4",
  badgeLabel: "h5",

  // Semantic defaults
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
};

/**
 * Heading Component
 *
 * A flexible heading component that separates semantic HTML structure
 * from visual styling through variants.
 *
 * @example
 * ```tsx
 * <Heading variant="pageTitle">Welcome</Heading>
 * <Heading variant="sectionTitle" as="h2">Latest Shows</Heading>
 * <Heading variant="cardTitle">Show Name</Heading>
 * ```
 */
export const Heading: ParentComponent<HeadingProps> = (props) => {
  const [local, others] = splitProps(props, [
    "variant",
    "as",
    "center",
    "class",
    "classList",
    "children",
  ]);

  const variant = () => local.variant || "h2";
  const element = () => local.as || variantToElementMap[variant()];

  return (
    <Dynamic
      component={element()}
      class={styles.heading}
      classList={{
        [styles.center]: local.center,
        [styles[variant()]]: true,
        [local.class || ""]: !!local.class,
        ...local.classList,
      }}
      {...others}
    >
      {local.children}
    </Dynamic>
  );
};

export default Heading;
